import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import useEscapeKey from '../hooks/useEscapeKey';
import './TimeSelectDropdown.css';

/**
 * TimeSelectDropdown — Google-calendar-style single time picker.
 *
 * A compact trigger button shows the current time (e.g. "9:00am"). Clicking it
 * opens a scrollable list of every time on a fixed grid (15-min steps by default)
 * across the full 24h day; one click selects. The open list is portalled to
 * document.body and positioned from the trigger's rect, so it's never clipped
 * by a modal's overflow. Closes on select, Escape, and outside mousedown.
 *
 * A brand-new component, entirely separate from TimeInputWithPicker (the
 * three-select hour/minute/AM-PM picker used by the ticket wizard and slot
 * modals). Self-contained (manages its own open state) so a form can drop in
 * many of these — e.g. a start + end per weekday — without threading
 * open/position state per instance.
 *
 * Value in/out is always a 24h "HH:MM" string.
 *
 * @param {Object}   props
 * @param {string}   props.value       - "HH:MM" (24h) or empty
 * @param {Function} props.onChange    - (hhmm) => void
 * @param {number}   [props.step=15]   - Minute increment for the list
 * @param {boolean}  [props.error]     - Error styling on the trigger
 * @param {string}   [props.ariaLabel] - Accessible label for the trigger
 * @param {string}   [props.placeholder='Set time'] - Shown when value is empty
 * @param {string}   [props.accent='turquoise'] - 'turquoise' | 'purple' theme accent
 * @param {number}   [props.zIndex] - Override the portalled list's z-index (default 1200 via CSS). Needed inside modals whose overlay z-index exceeds 1200.
 */
const TimeSelectDropdown = ({
    value = '',
    onChange,
    step = 15,
    error = false,
    ariaLabel,
    placeholder = 'Set time',
    accent = 'turquoise',
    zIndex,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState(null); // { top, left, width }
    const triggerRef = useRef(null);
    const listRef = useRef(null);

    useEscapeKey(isOpen, () => setIsOpen(false));

    // Build the time grid once per step: every HH:MM from 00:00 to 23:(60-step).
    // A value off the grid (e.g. an imported 7:50) is spliced in so it can be
    // shown as selected rather than rendering nothing.
    const options = useMemo(() => {
        const opts = [];
        for (let m = 0; m < 24 * 60; m += step) {
            opts.push(minutesToHHMM(m));
        }
        if (value && !opts.includes(value)) {
            opts.push(value);
            opts.sort();
        }
        return opts;
    }, [step, value]);

    // Position the portalled list under the trigger, matched to its width.
    const computePosition = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return null;
        return {
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: rect.width,
        };
    };

    const openList = () => {
        const pos = computePosition();
        if (pos) setPosition(pos);
        setIsOpen(true);
    };

    // Keep the portalled list anchored to the trigger while open — a scrolling
    // ancestor (e.g. a modal body) moves the trigger without moving the list.
    // Capture-phase scroll listener catches inner scroll containers too.
    useEffect(() => {
        if (!isOpen) return undefined;
        const reposition = () => {
            const pos = computePosition();
            if (pos) setPosition(pos);
        };
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Close on an outside mousedown (matches the codebase's portal-dropdown pattern).
    useEffect(() => {
        if (!isOpen) return undefined;
        const handleClickOutside = (e) => {
            if (
                !e.target.closest('.time-select-dropdown-list') &&
                !e.target.closest('.time-select-dropdown-trigger')
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Centre the selected option in the list when it opens. Scrolls ONLY the
    // list's own scrollbar — scrollIntoView() would also scroll every
    // scrollable ancestor (the page, a modal body), yanking the background
    // and bouncing the portalled list against its reposition listener.
    useLayoutEffect(() => {
        if (!isOpen || !listRef.current) return;
        const list = listRef.current;
        const selected = list.querySelector('.time-select-option.selected');
        if (selected) {
            list.scrollTop = selected.offsetTop - list.clientHeight / 2 + selected.clientHeight / 2;
        }
    }, [isOpen]);

    const handleSelect = (hhmm) => {
        onChange(hhmm);
        setIsOpen(false);
    };

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                className={`time-select-dropdown-trigger time-select-dropdown-trigger--${accent}${error ? ' error' : ''}${isOpen ? ' open' : ''}`}
                onClick={() => (isOpen ? setIsOpen(false) : openList())}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                {value ? formatTimeDisplay(value) : placeholder}
            </button>

            {isOpen &&
                position &&
                ReactDOM.createPortal(
                    <div
                        ref={listRef}
                        className={`time-select-dropdown-list time-select-dropdown-list--${accent}`}
                        role="listbox"
                        style={{ top: position.top, left: position.left, minWidth: position.width, ...(zIndex ? { zIndex } : {}) }}
                    >
                        {options.map((hhmm) => (
                            <button
                                type="button"
                                key={hhmm}
                                role="option"
                                aria-selected={hhmm === value}
                                className={`time-select-option${hhmm === value ? ' selected' : ''}`}
                                onClick={() => handleSelect(hhmm)}
                            >
                                {formatTimeDisplay(hhmm)}
                            </button>
                        ))}
                    </div>,
                    document.body
                )}
        </>
    );
};

/** Minutes-since-midnight → "HH:MM" (24h). */
function minutesToHHMM(mins) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** "HH:MM" (24h) → Google-style "9:00am" / "12:30pm". */
function formatTimeDisplay(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    const period = h >= 12 ? 'pm' : 'am';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour}:${String(m).padStart(2, '0')}${period}`;
}

export default TimeSelectDropdown;
